import { useMutation } from '@apollo/client';
import { FormProvider } from 'react-hook-form';
import { toast } from 'react-toastify';
import type { SoManagerReservoirModelPartsFragment } from '~/apollo/generated/v4/graphql';
import { Button } from '~/components/ui/button';
import { HookFormErrors } from '~/components/ui/forms/HookFormErrors';
import { SOFormFields } from '~/components/upload/supporting-object/so-form-fields';
import type { SOParent } from '~/components/upload/supporting-object/so-manager';
import { UPDATE_RESERVOIR_MODEL } from '~/components/upload/supporting-object/so-operations';
import { useZodForm } from '~/utils/forms';
import {
  initialReservoirModel,
  reservoirModelSchema,
} from '~/utils/modules/supporting-object';

type Props = SOParent & {
  reservoirModel: SoManagerReservoirModelPartsFragment;
  onUpdateSuccess?: () => unknown;
};

export function ReservoirModelUpdateForm({
  reservoirModel,
  studyId,
  onUpdateSuccess,
}: Props) {
  const [updateReservoirModel, { loading, error }] = useMutation(
    UPDATE_RESERVOIR_MODEL,
  );

  const form = useZodForm({
    schema: reservoirModelSchema,
    values: initialReservoirModel(reservoirModel),
  });

  const handleSubmit = form.handleSubmit(async values => {
    try {
      await updateReservoirModel({
        variables: {
          id: reservoirModel.id,
          input: values,
        },
      });
      toast.success('Reservoir model updated successfully.');
      form.reset();
      if (onUpdateSuccess) onUpdateSuccess();
    } catch (err) {
      console.log('Error updating reservoir model', err);
      toast.error('There was a problem updating the reservoir model.');
    }
  });

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <SOFormFields soType="RESERVOIR_MODEL" studyId={studyId} />
        <HookFormErrors graphQLError={error} />

        <div className="card-actions justify-end">
          <Button type="submit" loading={loading} color="primary">
            Save
          </Button>
        </div>
      </form>
    </FormProvider>
  );
}
